/* "Swatch" option inside the Size picker.
 *
 * The swatch used to be a real variant on 1,181 rugs. Shopify publishes a
 * product's cheapest variant as its "from" price, so every rug advertised at
 * $76.99 across Google, Meta and Pinterest. The variant is gone; this orders
 * one on demand from POST /api/ruggen/swatch/checkout/, which mints a hidden
 * one-off product and returns its variant id.
 *
 * The entry point sits in the Size picker because that is where the shopper is
 * already deciding what to order, and it behaves like the other options there:
 * clicking it SELECTS the swatch and shows its price, and the theme's own Add
 * to cart is what actually adds it.
 *
 * The injection and cart-add contracts here intentionally mirror
 * assets/custom-size-rug.js. They are duplicated rather than shared so this can
 * ship without touching that verified widget; see task 010 for the extraction.
 */
(function () {
  if (window.__eurusSwatchOrderLoaded) return;
  window.__eurusSwatchOrderLoaded = true;

  /* Custom size and Swatch are both injected into the Size picker, and each
   * used to track its own state, so a shopper could have BOTH reading as
   * selected. They are alternatives — only one option in that row can be the
   * chosen one. Each announces its selection and clears itself when it hears
   * another, so neither file has to reach into the other's internals, and a
   * third injected option would drop in the same way. */
  var SIZE_OPTION_EVENT = 'eurus:size-option:selected';


  /* Clicking the size that is ALREADY checked fires NO change event, so a
   * change listener alone leaves the injected option selected — landing on
   * ?variant=<12'x15'> and then clicking 12' x 15' read as a dead option.
   * A click on a real size option means "I want that size" whether or not the
   * radio state actually moves. */
  function isRealSizeOptionClick(e) {
    var el = e.target;
    if (!el || !el.closest) return false;
    if (el.closest('[data-sw-option], [data-cs-size-btn]')) return false;
    var radio = null;
    if (el.matches && el.matches('input[type="radio"]')) {
      radio = el;
    } else {
      var label = el.closest('label[for]');
      if (label) radio = document.getElementById(label.htmlFor);
    }
    return !!(radio && /^size(-\d+)?\s*$/i.test(radio.name || ''));
  }

  function readJson(root, selector) {
    var el = root.querySelector(selector);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /* Eurus's own cart-add contract — the same one assets/product-cart.js uses.
   * The shopper stays on the page and the drawer opens over it. Deliberately
   * NOT the cart permalink the API also returns: a permalink navigates to the
   * .myshopify.com domain AND replaces the cart, discarding whatever the
   * shopper had already added. */
  function themeCartAvailable() {
    return !!(window.Alpine && window.Alpine.store && window.Alpine.store('xCartHelper') &&
      window.Eurus && window.Eurus.cart_add_url && typeof getSectionInnerHTML === 'function');
  }

  function addVariantToThemeCart(variantId, qty) {
    var sections = window.Alpine.store('xCartHelper').getSectionsToRender();
    var formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', String(qty || 1));
    formData.append('sections', sections.map(function (s) { return s.id; }));
    formData.append('sections_url', window.location.pathname);

    return fetch(window.Eurus.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    }).then(function (r) { return r.json(); }).then(function (response) {
      if (String(response.status) === '422') {
        throw new Error(response.description || response.message || 'Add to cart failed');
      }
      sections.forEach(function (section) {
        var el = document.querySelector(section.selector);
        if (el && response.sections && response.sections[section.id]) {
          el.innerHTML = getSectionInnerHTML(response.sections[section.id], section.selector);
        }
      });
      if (window.Alpine.store('xMiniCart')) window.Alpine.store('xMiniCart').openCart();
      var bubble = document.querySelector('#cart-icon-bubble span');
      if (bubble) window.Alpine.store('xCartHelper').currentItemCount = parseInt(bubble.innerHTML, 10);
      document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
      return response;
    });
  }

  function addThenGoToCart(variantId) {
    var formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', '1');
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    }).then(function (r) { return r.json(); }).then(function (body) {
      if (body && body.status && String(body.status) !== '200') {
        throw new Error(body.description || body.message || 'Add to cart failed');
      }
      window.location.href = '/cart';
    });
  }

  function sizeOptionLabels() {
    var radios = Array.prototype.slice.call(document.querySelectorAll('input[type="radio"]'))
      .filter(function (r) { return /^size(-\d+)?\s*$/i.test(r.name || ''); });
    return radios.map(function (r) {
      return document.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(r.id) : r.id) + '"]');
    }).filter(function (l) { return l && l.offsetParent !== null; });
  }

  /* CLONE a real size option rather than building a <button>. Same element
   * type, same classes, same markup — so it cannot drift from the theme. A
   * <button> is not interchangeable: this theme styles buttons (and
   * [role="button"]) as 100px pills with centred text, which would render as a
   * pill among square size chips. The two properties that leak through are
   * pinned to whatever a real option computes to. */
  /* Keep the two injected options together, in a stable order: Custom size
   * first, Swatch immediately after it. custom-size-rug.js and this file each
   * append independently and either can win the race, so position is settled
   * on every re-injection pass rather than trusting script boot order. */
  function placeAfterCustomSize(container, opt) {
    var customSize = container.querySelector('[data-cs-size-btn]');
    if (customSize) {
      if (opt.previousSibling === customSize) return;
      container.insertBefore(opt, customSize.nextSibling);
      return;
    }
    if (opt.parentElement !== container) container.appendChild(opt);
  }

  function injectSwatchOption(config, onActivate) {
    var labels = sizeOptionLabels();
    if (!labels.length) return false;
    var container = labels[0].parentElement;
    if (!container) return false;
    var existing = container.querySelector('[data-sw-option]');
    if (existing) {
      placeAfterCustomSize(container, existing);
      return true;
    }

    // Clone an UNSELECTED option so the resting appearance is the resting one.
    var ref = labels.filter(function (l) {
      var input = l.htmlFor ? document.getElementById(l.htmlFor) : null;
      return input && !input.checked;
    })[0] || labels[0];
    var opt = ref.cloneNode(true);
    opt.removeAttribute('for');
    opt.removeAttribute('id');

    var refSpan = ref.querySelector('span');
    opt.innerHTML = '';
    var span = document.createElement('span');
    if (refSpan && refSpan.className) span.className = refSpan.className;
    span.textContent = config.optionLabel || 'Swatch';
    opt.appendChild(span);

    opt.classList.add('sw-order__option');
    opt.setAttribute('data-sw-option', '');
    var refStyle = getComputedStyle(ref);
    opt.style.borderRadius = refStyle.borderRadius;
    opt.style.color = refStyle.color;
    opt.setAttribute('role', 'button');
    opt.setAttribute('tabindex', '0');

    opt.addEventListener('click', function () { onActivate(span); });
    opt.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        onActivate(span);
      }
    });
    placeAfterCustomSize(container, opt);
    return true;
  }

  function initWidget(root) {
    var config = readJson(root, '[data-sw-config]') || {};
    var error = root.querySelector('[data-sw-error]');
    var busy = false;
    var selected = false;
    var originalPriceHTML = null;

    function endpoint() {
      var path = 'swatch/checkout/';
      if (config.proxyPath) return config.proxyPath.replace(/\/$/, '') + '/' + path;
      return (config.apiBaseUrl || '').replace(/\/$/, '') + '/api/ruggen/' + path;
    }

    function track(name, data) {
      if (window.dataLayer && window.dataLayer.push) {
        window.dataLayer.push(Object.assign({ event: 'swatch_' + name }, data || {}));
      }
    }

    function formatMoney(amount) {
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency', currency: config.currency || 'USD'
        }).format(amount);
      } catch (e) { return '$' + Number(amount).toFixed(2); }
    }

    /* The PDP and the quick-view drawer BOTH render a button[name="add"], and
     * quick-view's comes first in the DOM — a global lookup lands on the wrong
     * one and the shopper sees no feedback at all, so the click reads as dead.
     * Always drive the button that was actually clicked.
     *
     * The theme already has a spinner on this button, bound to an Alpine
     * `loading` flag (the label span carries :class="loading && 'invisible'").
     * Use that rather than rewriting the label, so the busy state looks exactly
     * like a normal add. */
    function setBusy(btn, on) {
      if (!btn) return;
      btn.disabled = on;
      var data = null;
      try {
        data = window.Alpine && window.Alpine.$data ? window.Alpine.$data(btn) : null;
      } catch (e) { data = null; }
      if (data && typeof data.loading !== 'undefined') {
        data.loading = on;
        return;
      }
      // Alpine not booted — mirror what its binding would have done.
      var label = btn.querySelector('.x-atc-text');
      if (!label) return;
      if (on) {
        label.setAttribute('data-sw-prev', label.textContent);
        label.textContent = config.busyLabel;
      } else if (label.hasAttribute('data-sw-prev')) {
        label.textContent = label.getAttribute('data-sw-prev');
        label.removeAttribute('data-sw-prev');
      }
    }

    /* The template renders MORE THAN ONE .main-product-price — the PDP copy
     * plus quick-view/sticky copies. Picking "the laid-out one" was
     * viewport-dependent: whichever happened to be on screen won, so the
     * swatch price could land in a node the shopper was not looking at.
     * They all display the same product price, so update every one and
     * restore each from its own saved markup. */
    function priceEls() {
      return Array.prototype.slice.call(document.querySelectorAll('.main-product-price'));
    }

    /* Selecting the swatch is a SIZE CHOICE, not a purchase: it swaps the
     * displayed price and lets the theme's own Add to cart do the adding, the
     * same as any other size. The swatch is not a real variant, so the form
     * would submit whatever size is still checked — the capture-phase listener
     * below intercepts that and runs the swatch order instead. */
    function select() {
      if (selected) return;
      selected = true;
      var opt = document.querySelector('[data-sw-option]');
      var container = opt ? opt.parentElement : null;
      if (container) container.classList.add('sw-order-active');
      if (opt) opt.setAttribute('aria-pressed', 'true');
      var els = priceEls();
      if (originalPriceHTML === null) {
        originalPriceHTML = els.map(function (e) { return e.innerHTML; });
      }
      els.forEach(function (e) { e.textContent = formatMoney(config.price); });
      if (error) error.hidden = true;
      document.dispatchEvent(new CustomEvent(SIZE_OPTION_EVENT, {
        detail: { source: 'swatch' }
      }));
      track('selected', { product_id: config.productId });
    }

    function deselect() {
      if (!selected) return;
      selected = false;
      var opt = document.querySelector('[data-sw-option]');
      var container = opt ? opt.parentElement : null;
      if (container) container.classList.remove('sw-order-active');
      if (opt) opt.setAttribute('aria-pressed', 'false');
      if (originalPriceHTML !== null) {
        priceEls().forEach(function (e, i) {
          if (originalPriceHTML[i] !== undefined) e.innerHTML = originalPriceHTML[i];
        });
      }
      originalPriceHTML = null;
    }

    function fail(btn, message) {
      busy = false;
      setBusy(btn, false);
      if (!error) return;
      error.textContent = message || config.errorText;
      error.hidden = false;
    }

    function order(btn) {
      if (busy) return;
      busy = true;
      if (error) error.hidden = true;
      setBusy(btn, true);
      track('add_to_cart', { product_id: config.productId });

      fetch(endpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: config.productId, quantity: 1 })
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (body) {
          if (!r.ok) throw new Error(body.error || Object.values(body || {})[0] || config.errorText);
          return body;
        });
      }).then(function (res) {
        // A MOCK_VARIANT url means the backend has no store credentials —
        // never send a live shopper to that dead cart link.
        if (res.checkout_url && res.checkout_url.indexOf('MOCK_VARIANT') > -1) {
          throw new Error(config.errorText);
        }
        if (!res.variant_id) throw new Error(config.errorText);
        var finish = function () {
          busy = false;
          setBusy(btn, false);
          track('added', { variant_id: res.variant_id });
        };
        if (themeCartAvailable()) {
          return addVariantToThemeCart(res.variant_id, 1).then(finish).catch(function () {
            return addThenGoToCart(res.variant_id);
          });
        }
        return addThenGoToCart(res.variant_id);
      }).catch(function (err) {
        fail(btn, err && err.message);
      });
    }

    injectSwatchOption(config, function () { selected ? deselect() : select(); });

    /* Capture phase, and stopImmediatePropagation: the theme binds its own
     * submit handler to this button, so letting the event through would add
     * the still-checked size variant alongside the swatch. */
    document.addEventListener('click', function (e) {
      if (!selected || busy) return;
      var el = e.target;
      var btn = el && el.closest ? el.closest('button[name="add"], .add_to_cart_button') : null;
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      order(btn);
    }, true);

    document.addEventListener(SIZE_OPTION_EVENT, function (e) {
      if (e.detail && e.detail.source !== 'swatch') deselect();
    });

    /* Choosing a real size means the shopper no longer wants the swatch. The
     * theme re-renders the section on variant change, which restores the price
     * markup on its own; this just drops the selected state. */
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.name && /^size(-\d+)?\s*$/i.test(t.name)) deselect();
    }, true);

    document.addEventListener('click', function (e) {
      if (selected && isRealSizeOptionClick(e)) deselect();
    }, true);

    // The theme swaps the variant picker out on every size change, taking the
    // injected option with it; put it back whenever that happens.
    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () {
        pending = null;
        injectSwatchOption(config, function () { selected ? deselect() : select(); });
      }, 0);
    }).observe(document.body, { childList: true, subtree: true });
  }

  function boot() {
    document.querySelectorAll('[data-swatch-order]').forEach(initWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
